import { TrashIcon } from '@heroicons/react/outline';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAsyncValue } from '@zedoc/react-hooks';
import { ADMIN_DELETE_PROJECT } from '../../../common/permissions';
import Highlighter from '../../../common/components/Highlighter';
import ProjectSelect from '../../../common/selectors/Project';
import createHttpClient from '../../../common/controllers/createHttpClient';
import BlueprintDraft from '../../../common/sdk/blueprints/BlueprintDraft';
import usePermission from '../../../utils/usePermission';
import usePinItem from '../../../utils/usePinItem';
import Button from '../../../components/Button';
import { Pin } from '../../../ui/icons';
import Tooltip from '../../../common/components/Tooltip';
import formatRelativeTimeAgo from '../../SettingsProject_v2/components/formatRelativeTimeAgo';

const apiClient = createHttpClient('/api/');

const ProjectItem = React.memo(
  ({
    name,
    description,
    error,
    projectId,
    draftSavedAt,
    onDelete,
    highlight,
  }) => {
    const { t } = useTranslation();
    const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));
    const canDeleteProjects = usePermission([ADMIN_DELETE_PROJECT], {
      relativeTo: project && project.getDomains(),
    });
    const handleDeleteProject = useCallback(() => {
      if (onDelete) {
        onDelete(projectId);
      }
    }, [projectId, onDelete]);
    const { blueprintId } = project || {};
    const {
      value: latestDraft,
      // ready: latestDraftReady,
    } = useAsyncValue(async () => {
      const client = apiClient.login();
      return BlueprintDraft.getMaxVersionSatisfying(blueprintId, 'x', client);
    }, [blueprintId]);
    const getBlueprintUrl = useCallback(() => {
      let pathname = `/settings/blueprints/${blueprintId}`;

      if (latestDraft && latestDraft.props.version) {
        pathname += `?version=${latestDraft.props.version}`;
      }

      return pathname;
    }, [blueprintId, latestDraft]);

    const { isPinned, isLoading, handleOnHighlightProject } = usePinItem({
      id: project._id,
      namespace: 'projects',
    });
    const [, setNow] = useState(Date.now());
    const resolvedDraftSavedAt = draftSavedAt || project?.draftSavedAt;
    const hasDraft = !!resolvedDraftSavedAt;
    const draftSavedLabel = formatRelativeTimeAgo(resolvedDraftSavedAt);

    useEffect(() => {
      if (!hasDraft) {
        return undefined;
      }
      const intervalId = setInterval(() => {
        setNow(Date.now());
      }, 30000);
      return () => clearInterval(intervalId);
    }, [hasDraft]);

    return (
      <div className="cluster-4 items-center">
        <div className="stack-0 flex-1">
          <div className="cluster-3 items-center flex-wrap">
            <Link
              data-testid={`item-${name}`}
              to={
                blueprintId
                  ? getBlueprintUrl()
                  : `/settings/projects/${projectId}/project-variables`
              }
              className="text-lg text-primary-800 font-medium"
            >
              <Highlighter text={project.name} highlight={highlight} />
            </Link>
            {hasDraft && (
              <span
                data-testid={`label-draft-saved-at-${name}`}
                className="inline-flex items-center rounded-full border border-warning bg-warning-muted dark:bg-neutral-800 px-3 py-0.5 text-xs font-medium text-on-warning dark:text-warning whitespace-nowrap"
              >
                {draftSavedLabel
                  ? `Changes not published | ${draftSavedLabel}`
                  : 'Changes not published'}
              </span>
            )}
            <Button
              type="ghost"
              size="medium"
              onClick={handleOnHighlightProject}
              loading={isLoading}
              className="flex-shrink-0"
              icon={
                <Pin
                  className={
                    isPinned ? '' : 'text-neutral-700 dark:text-neutral-400'
                  }
                />
              }
            />
          </div>
          {error && <p className="text-danger">{error}</p>}
          {description && <p>{description}</p>}
        </div>
        <div className="cluster-4">
          <Tooltip title={t('remove')}>
            <Button
              data-testid={`button-delete-${name}`}
              type="ghost"
              icon={<TrashIcon />}
              disabled={!canDeleteProjects}
              onClick={handleDeleteProject}
            />
          </Tooltip>
        </div>
      </div>
    );
  },
);

ProjectItem.propTypes = {
  name: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  draftSavedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  description: PropTypes.string,
  error: PropTypes.string,
  onDelete: PropTypes.func,
  highlight: PropTypes.arrayOf(PropTypes.string),
};

ProjectItem.defaultProps = {
  description: null,
  draftSavedAt: null,
  error: null,
  onDelete: null,
  highlight: [],
};

export default ProjectItem;
